'use client';

import { Checkbox, MenuItem, Select } from '@/primitives';
import { setSearchParam } from '@/components/entity/helpers';
import {
  SALE_DATE_FILTER_PARAM,
  SALE_TYPE_FILTER_PARAM,
  SALE_TYPE_MULTI_OPTIONS,
  useCatalogueFilters,
} from '@/hooks/publicSearch/useCatalogueFilters';
import { slate } from '@/lib/colours';
import { CatalogueSaleTypeOption } from '../constants';

const catalogueSaleTypeOptions = Object.values(
  CatalogueSaleTypeOption,
) as Array<{ label: string; value: string }>;

export default function CatalogueFiltersControl({
  isMobile,
}: {
  isMobile: boolean;
}) {
  const { saleDateFilter, saleTypeFilterArray } = useCatalogueFilters();

  return (
    <>
      <Select
        size="small"
        multiple
        value={saleTypeFilterArray}
        displayEmpty
        onChange={(event) => {
          const value = event.target.value;
          const selected =
            typeof value === 'string' ? value.split(',') : (value as string[]);
          setSearchParam(
            SALE_TYPE_FILTER_PARAM,
            selected.length ? selected.join(',') : undefined,
          );
          // URL changes drive the catalogue query. Calling startSearch here
          // would use the previous render's filters and race the correct request.
        }}
        renderValue={(selected) => {
          if (!selected || (selected as string[]).length === 0) {
            return 'Sale Type';
          }
          const labels = (selected as string[]).map(
            (code) =>
              catalogueSaleTypeOptions.find((option) => option.value === code)
                ?.label ?? code,
          );
          return labels.length > 2
            ? `${labels.length} types`
            : labels.join(', ');
        }}
        sx={{
          flex: isMobile ? 1 : undefined,
          minWidth: isMobile ? 0 : 120,
          height: isMobile ? 32 : 36,
          borderRadius: '8px',
          backgroundColor: 'background.paper',
          fontSize: '13px',
          fieldset: {
            border: `${isMobile ? '0.5px' : '1.5px'} solid ${slate[300]}`,
          },
        }}
        MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
      >
        {SALE_TYPE_MULTI_OPTIONS.map((code) => {
          const label =
            catalogueSaleTypeOptions.find((option) => option.value === code)
              ?.label ?? code;
          return (
            <MenuItem key={code} value={code}>
              <Checkbox
                size="small"
                checked={saleTypeFilterArray.includes(code)}
                sx={{ mr: 1 }}
              />
              {label}
            </MenuItem>
          );
        })}
      </Select>
      <Select
        size="small"
        value={saleDateFilter}
        onChange={(event) => {
          setSearchParam(SALE_DATE_FILTER_PARAM, event.target.value as string);
        }}
        sx={{
          minWidth: isMobile ? 96 : 100,
          height: isMobile ? 32 : 36,
          borderRadius: '8px',
          backgroundColor: 'background.paper',
          fontSize: '13px',
          fieldset: {
            border: `${isMobile ? '0.5px' : '1.5px'} solid ${slate[300]}`,
          },
        }}
      >
        <MenuItem value="current">Current</MenuItem>
        <MenuItem value="all">All</MenuItem>
      </Select>
    </>
  );
}
